import React, { useState, useRef, useEffect } from "react";
import Icon from "../../../components/AppIcon";

const ConsoleQuery = ({ data }) => {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([
    {
      type: "system",
      content: 'Developer Console v2.1.0 - Type "help" for available commands',
    },
    {
      type: "system",
      content:
        "Try: developer.name, developer.skills.backend, developer.experience[0].company",
    },
  ]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [commandHistory, setCommandHistory] = useState([]);
  const inputRef = useRef(null);
  const consoleRef = useRef(null);

  useEffect(() => {
    if (consoleRef?.current) {
      consoleRef.current.scrollTop = consoleRef?.current?.scrollHeight;
    }
  }, [history]);

  const getNestedValue = (obj, path) => {
    try {
      const keys = path?.split(".");
      let current = obj;

      for (let key of keys) {
        if (key?.includes("[") && key?.includes("]")) {
          const arrayKey = key?.split("[")?.[0];
          const index = parseInt(key?.split("[")?.[1]?.split("]")?.[0]);
          current = current?.[arrayKey]?.[index];
        } else {
          current = current?.[key];
        }
      }

      return current;
    } catch (error) {
      return undefined;
    }
  };

  const formatOutput = (value) => {
    if (value === undefined) {
      return {
        type: "error",
        content: "Property not found. Did you check the documentation? 😅",
      };
    }

    if (value === null) {
      return { type: "output", content: "null" };
    }

    if (typeof value === "object") {
      return { type: "output", content: JSON.stringify(value, null, 2) };
    }

    return { type: "output", content: String(value) };
  };

  const executeCommand = (command) => {
    const trimmedCommand = command?.trim();

    if (!trimmedCommand) return;

    setHistory((prev) => [
      ...prev,
      { type: "input", content: `> ${trimmedCommand}` },
    ]);

    if (trimmedCommand === "help") {
      setHistory((prev) => [
        ...prev,
        { type: "system", content: "Available commands:" },
        {
          type: "system",
          content: "• developer.* - Access developer properties",
        },
        { type: "system", content: "• clear - Clear console" },
        { type: "system", content: "• help - Show this help" },
        { type: "system", content: "• whoami - Current developer info" },
      ]);
      return;
    }

    if (trimmedCommand === "clear") {
      setHistory([
        {
          type: "system",
          content: "Console cleared. Ready for more debugging! 🐛",
        },
      ]);
      return;
    }

    if (trimmedCommand === "whoami") {
      setHistory((prev) => [
        ...prev,
        {
          type: "output",
          content: "Backend Developer transitioning to Full-Stack",
        },
        {
          type: "output",
          content: "Current status: Learning CSS without crying",
        },
      ]);
      return;
    }

    if (trimmedCommand?.startsWith("developer.")) {
      const path = trimmedCommand?.replace("developer.", "");
      const result = getNestedValue(data, path);
      const output = formatOutput(result);
      setHistory((prev) => [...prev, output]);
      return;
    }

    // Easter eggs
    if (
      trimmedCommand?.includes("css") ||
      trimmedCommand?.includes("frontend")
    ) {
      setHistory((prev) => [
        ...prev,
        {
          type: "error",
          content: "Warning: Frontend skills still loading... ⏳",
        },
      ]);
      return;
    }

    if (trimmedCommand === "sudo make me a sandwich") {
      setHistory((prev) => [
        ...prev,
        {
          type: "output",
          content: "🥪 Here's your sandwich! (Backend developers need fuel)",
        },
      ]);
      return;
    }

    setHistory((prev) => [
      ...prev,
      { type: "error", content: `Command not found: ${trimmedCommand}` },
      { type: "system", content: 'Type "help" for available commands' },
    ]);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input?.trim()) {
      setCommandHistory((prev) => [...prev, input]);
      setHistoryIndex(-1);
      executeCommand(input);
      setInput("");
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === "ArrowUp") {
      e?.preventDefault();
      if (commandHistory?.length > 0) {
        const newIndex =
          historyIndex === -1
            ? commandHistory?.length - 1
            : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInput(commandHistory?.[newIndex]);
      }
    } else if (e?.key === "ArrowDown") {
      e?.preventDefault();
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory?.length) {
          setHistoryIndex(-1);
          setInput("");
        } else {
          setHistoryIndex(newIndex);
          setInput(commandHistory?.[newIndex]);
        }
      }
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden terminal-glow">
      <div className="bg-muted/30 px-4 py-2 border-b border-border flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-destructive rounded-full"></div>
            <div className="w-3 h-3 bg-warning rounded-full"></div>
            <div className="w-3 h-3 bg-success rounded-full"></div>
          </div>
          <span className="text-sm font-source text-muted-foreground">
            Developer Console
          </span>
        </div>
        <div className="hidden sm:flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Terminal" size={14} />
          <span>Interactive Mode</span>
        </div>
      </div>
      <div
        ref={consoleRef}
        className="h-64 overflow-y-auto p-4 bg-background/50 font-source text-sm"
      >
        {history?.map((entry, index) => (
          <div
            key={index}
            className={`mb-1 ${
              entry?.type === "input"
                ? "text-foreground"
                : entry?.type === "output"
                ? "text-terminal-cyan"
                : entry?.type === "error"
                ? "text-destructive"
                : "text-muted-foreground"
            }`}
          >
            <pre className="whitespace-pre-wrap font-source">
              {entry?.content}
            </pre>
          </div>
        ))}
      </div>
      <form onSubmit={handleSubmit} className="border-t border-border p-4">
        <div className="flex items-center space-x-2">
          <span className="text-[12px] font-source">{">"}</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e?.target?.value)}
            onKeyDown={handleKeyDown}
            placeholder="Try: developer.skills.javascript"
            className="flex-1 bg-transparent border outline-none text-foreground font-source placeholder:text-muted-foreground focus:border-[#00ff40] focus:ring-0 rounded"
            autoComplete="off"
          />
          <button
            type="submit"
            className="text-primary hover:text-primary/80 transition-colors"
          >
            <Icon name="Play" size={16} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConsoleQuery;
